import React, { useCallback, useState } from "react";
import Cropper from "react-easy-crop";
import { Check, X, ZoomIn, ZoomOut, RotateCcw, Scissors } from "lucide-react";

/**
 * Centered square-crop modal with optional client-side background removal.
 * Accepts a File, produces a cropped (and optionally bg-removed) PNG File on confirm.
 */
async function getCroppedFile(imageSrc, cropPixels, rotation = 0, outSize = 1024) {
  const image = await new Promise((res, rej) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = imageSrc;
  });
  const canvas = document.createElement("canvas");
  canvas.width = outSize;
  canvas.height = outSize;
  const ctx = canvas.getContext("2d");
  // Transparent canvas so post-processing keeps an alpha channel
  ctx.clearRect(0, 0, outSize, outSize);

  if (rotation) {
    ctx.translate(outSize / 2, outSize / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.translate(-outSize / 2, -outSize / 2);
  }
  ctx.drawImage(
    image,
    cropPixels.x,
    cropPixels.y,
    cropPixels.width,
    cropPixels.height,
    0,
    0,
    outSize,
    outSize
  );
  const blob = await new Promise((res) => canvas.toBlob(res, "image/png", 0.92));
  return new File([blob], "selfie.png", { type: "image/png" });
}

// Lazy-load @imgly/background-removal so the ~14MB model only downloads when needed
let _bgRemoveFn = null;
async function loadBgRemoval() {
  if (_bgRemoveFn) return _bgRemoveFn;
  const mod = await import("@imgly/background-removal");
  _bgRemoveFn = mod.default || mod.removeBackground || mod;
  return _bgRemoveFn;
}

async function removeBackground(file) {
  const remove = await loadBgRemoval();
  const blob = await remove(file, {
    output: { format: "image/png", quality: 0.9 },
  });
  return new File([blob], "selfie-cutout.png", { type: "image/png" });
}

export default function CropModal({ file, onCancel, onConfirm }) {
  const [imageSrc] = useState(() => URL.createObjectURL(file));
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [cropPixels, setCropPixels] = useState(null);
  const [working, setWorking] = useState(false);
  const [statusLabel, setStatusLabel] = useState("");
  const [bgRemove, setBgRemove] = useState(false);

  const onCropComplete = useCallback((_area, areaPixels) => {
    setCropPixels(areaPixels);
  }, []);

  const confirm = async () => {
    if (!cropPixels) return;
    setWorking(true);
    setStatusLabel("Cropping…");
    try {
      let result = await getCroppedFile(imageSrc, cropPixels, rotation);
      if (bgRemove && navigator.onLine) {
        setStatusLabel("Removing background… (first run downloads a small model)");
        try {
          result = await removeBackground(result);
        } catch (e) {
          // Fall back to the plain crop if BG removal fails
          console.warn("Background removal failed, using crop only:", e);
        }
      }
      URL.revokeObjectURL(imageSrc);
      onConfirm(result);
    } catch (e) {
      setWorking(false);
      setStatusLabel("");
      alert("Could not process image: " + e.message);
    }
  };

  const cancel = () => {
    URL.revokeObjectURL(imageSrc);
    onCancel();
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-plum-deep/80 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="crop-modal"
    >
      <div className="glass-deep w-full max-w-2xl rounded-3xl p-5 sm:p-7">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="label-eyebrow">frame your portrait</p>
            <h3 className="font-display text-2xl text-ink">Center your royal face</h3>
          </div>
          <button
            onClick={cancel}
            className="rounded-full p-2 hover:bg-white/60"
            data-testid="crop-cancel-btn"
          >
            <X size={18} />
          </button>
        </div>

        <div
          className="relative w-full bg-blush rounded-2xl overflow-hidden"
          style={{ aspectRatio: "1 / 1", maxHeight: "55vh" }}
        >
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={1}
            cropShape="round"
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            objectFit="cover"
          />
        </div>

        <div className="grid grid-cols-[1fr_auto] gap-3 mt-4 items-center">
          <div className="flex items-center gap-2">
            <ZoomOut size={16} className="text-ink-soft" />
            <input
              type="range"
              min={1}
              max={4}
              step={0.05}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="flex-1 accent-[#D4AF37]"
              data-testid="crop-zoom-slider"
              aria-label="zoom"
            />
            <ZoomIn size={16} className="text-ink-soft" />
          </div>
          <button
            type="button"
            onClick={() => setRotation((r) => (r + 90) % 360)}
            className="btn-ghost-royal py-2 px-3 text-sm"
            data-testid="crop-rotate-btn"
            title="Rotate 90°"
          >
            <RotateCcw size={14} />
          </button>
        </div>

        {/* Background removal toggle */}
        <label className="mt-4 flex items-center gap-3 cursor-pointer select-none bg-white/60 rounded-2xl px-4 py-2.5" data-testid="bg-remove-row">
          <input
            type="checkbox"
            checked={bgRemove}
            onChange={(e) => setBgRemove(e.target.checked)}
            className="sr-only peer"
            data-testid="bg-remove-toggle"
          />
          <span className="relative w-10 h-5 bg-stone-300 rounded-full peer-checked:bg-[#D4AF37] transition-colors flex-shrink-0">
            <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow-md transition-transform ${bgRemove ? "translate-x-5" : ""}`}></span>
          </span>
          <Scissors size={14} className="text-[#B38728]" />
          <div className="min-w-0">
            <p className="font-display text-sm text-ink leading-tight">Clean cutout (remove photo background)</p>
            <p className="text-xs text-ink-soft leading-tight">
              First time runs on-device for a few seconds. Keeps only the face on the avatar body.
            </p>
          </div>
        </label>

        {working && statusLabel && (
          <p className="text-center text-xs text-ink-soft mt-3" data-testid="crop-status">{statusLabel}</p>
        )}

        <div className="flex gap-2 mt-4">
          <button
            type="button"
            onClick={cancel}
            disabled={working}
            className="btn-ghost-royal flex-1 py-3"
            data-testid="crop-cancel-action"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={confirm}
            disabled={working || !cropPixels}
            className="btn-royal flex-1 py-3"
            data-testid="crop-confirm-btn"
          >
            <Check size={16} /> {working ? (statusLabel || "Working…") : "Use this photo"}
          </button>
        </div>
      </div>
    </div>
  );
}
